import { useMemo, useState, type FormEvent } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { usePhoneField, useEmailField } from "../lib/useValidatedField";
import { SERVICE_AREA_ZIPS } from "../lib/validators";
import { api } from "../lib/api";

const PACKAGE_OPTIONS = [
  "Maintenance wash + interior wipe — $140",
  "Interior deep clean / extraction — $300",
  "Full detail, interior and exterior — $450",
  "1-step correction + sealant — $750",
  "New-car prep + ceramic coating — $1,200",
  "2-step correction + ceramic coating — $1,800",
  "Not sure — advise me",
];
const TIER_TO_PACKAGE = [0, 3, 5];

const SERVICES = [
  { n: "01", title: "Paint correction", body: "Single- and two-stage machine polishing under inspection lighting. Defects measured, not guessed." },
  { n: "02", title: "Ceramic coating", body: "Professional-grade coatings applied to prepared paint, glass, and wheels. Registered and warranted." },
  { n: "03", title: "Interior restoration", body: "Hot-water extraction, leather cleaning and conditioning, trim detail. Odour addressed at the source." },
  { n: "04", title: "Wheel and caliper refinish", body: "Faces and barrels decontaminated off the car where required, then coated against brake dust." },
  { n: "05", title: "Pre-sale and auction prep", body: "Presentation work ahead of listing, inspection, or consignment. Photography-ready on collection day." },
  { n: "06", title: "Storage and collection care", body: "Scheduled attention for vehicles that sit — dust removal, surface checks, coating maintenance." },
  { n: "07", title: "Maintenance wash programme", body: "Monthly or quarterly care for coated vehicles, on a standing appointment. Protection holds longer." },
];

const STEPS = [
  { n: "I", title: "Assessment", body: "Enquiry, photographs, and a paint history. The package and the price are agreed before the appointment." },
  { n: "II", title: "Preparation", body: "Decontamination wash, iron and tar removal, clay where needed. The surface is made neutral." },
  { n: "III", title: "Correction", body: "Machine polishing to the agreed stage, panel by panel, checked under inspection light." },
  { n: "IV", title: "Coating and cure", body: "Coating applied, levelled, and left to set. Aftercare notes and the warranty registration follow by email." },
];

export default function Home() {
  const phone = usePhoneField();
  const email = useEmailField();
  const [name, setName] = useState("");
  const [zip, setZip] = useState("");
  const [zipMsg, setZipMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [vehicle, setVehicle] = useState("");
  const [pkg, setPkg] = useState(PACKAGE_OPTIONS[3]);
  const [dateWindow, setDateWindow] = useState("Next week");
  const [utilities, setUtilities] = useState("both");
  const [notes, setNotes] = useState("");
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const onZipChange = (raw: string) => {
    const v = raw.replace(/\D/g, "").slice(0, 5);
    setZip(v);
    if (v.length < 5) {
      setZipMsg(null);
      return;
    }
    if (SERVICE_AREA_ZIPS.has(v)) {
      setZipMsg({ text: "Within the service area.", ok: true });
    } else {
      setZipMsg({ text: "Outside the 20-mile radius — send the enquiry anyway and the house will confirm.", ok: false });
    }
  };

  const required = useMemo(
    () => ({ name, phone: phone.value, email: email.value, zip, vehicle }),
    [name, phone.value, email.value, zip, vehicle],
  );

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const miss = new Set<string>();
    for (const [k, v] of Object.entries(required)) if (!v.trim()) miss.add(k);
    setMissing(miss);
    if (miss.size) return;
    if (!email.validate()) return;
    if (!phone.validate()) return;

    setSubmitError("");
    try {
      await api.post("/enquiries", {
        name,
        phone: phone.value,
        email: email.value,
        zip,
        vehicle,
        package: pkg,
        window: dateWindow,
        utilities,
        notes,
      });
      setSent(true);
    } catch {
      setSubmitError("Couldn't send that — call (410) 555-0118 and the house will take it down directly.");
    }
  };

  return (
    <>
      <SiteHeader variant="scroll" onHome />
      <main id="top">
        <section className="hero" data-screen-label="Hero">
          <div className="hero-media" />
          <div className="hero-scrim" />
          <div className="wrap hero-in">
            <p className="eyebrow">Ellicott City, Maryland · Mobile within 20 miles</p>
            <h1>Correction and coating, performed in your driveway.</h1>
            <p>
              Foundry Detailing is a mobile paint-correction and ceramic-coating studio. One vehicle a day, one
              technician, a controlled process brought to your address. Not a car wash.
            </p>
            <div className="hero-actions">
              <a href="#enquire" className="btn">
                Request an appointment
              </a>
              <a href="#work" className="btn btn--ghost">
                View the work
              </a>
            </div>
            <div className="hero-meta">
              <span>Fully mobile · Power and water supplied</span>
              <span>Coatings registered and warranted</span>
              <span>By appointment</span>
            </div>
          </div>
        </section>

        <section className="statement" data-screen-label="Positioning">
          <div className="wrap statement-grid">
            <div>
              <p className="eyebrow">The standard</p>
              <h2>A workshop process, delivered at your address.</h2>
            </div>
            <div>
              <p className="lede">
                A tunnel wash is an abrasive event. Correction is the opposite — a measured removal of a few microns
                of clear coat under controlled light, until the paint reads as it did new. Then it is protected.
              </p>
              <p className="lede">
                Every booking is assessed before arrival: paint condition, coating history, surface readiness. The
                quote is set in advance. Nothing is upsold on the driveway.
              </p>
            </div>
          </div>
        </section>

        <section id="services" data-screen-label="Services">
          <div className="wrap">
            <p className="eyebrow">Services</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-10)", maxWidth: "24ch" }}>What the house performs</h2>
            <div className="svc">
              {SERVICES.map((s) => (
                <div className="svc-item" key={s.n}>
                  <span className="mono">{s.n}</span>
                  <div>
                    <h3>{s.title}</h3>
                    <p>{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="process" className="process" data-theme="inverse" data-screen-label="Process">
          <div className="wrap">
            <p className="eyebrow" style={{ color: "var(--wg-brass-light)" }}>
              Process
            </p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-7)", maxWidth: "22ch" }}>
              Four stages, one day, one vehicle.
            </h2>
            <p className="lede">
              The schedule is deliberately thin. A correction and coating cannot be hurried, and a queue is how it
              gets hurried.
            </p>
            <div className="steps">
              {STEPS.map((s) => (
                <div className="step" key={s.n}>
                  <span className="mono" style={{ color: "var(--wg-brass-light)" }}>
                    {s.n}
                  </span>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </div>
              ))}
            </div>
            <p className="note">
              What is needed on site: a driveway, garage, or shaded flat surface with roughly two vehicle widths of
              clearance. Water and power are carried and supplied — if an exterior outlet or spigot is available, say
              so on the enquiry and it is used instead.
            </p>
          </div>
        </section>

        <section id="work" data-screen-label="Work">
          <div className="wrap">
            <p className="eyebrow">Work</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "24ch" }}>Before, and after correction</h2>
            <p className="lede">Drag the divider. Both frames are the same panel, the same light, ten hours apart.</p>
            <BeforeAfterSlider />
            <div className="gallery">
              <div className="gal-cell" />
              <div className="gal-cell" />
              <div className="gal-cell" />
            </div>
          </div>
        </section>

        <section className="reviews" data-screen-label="Reviews">
          <div className="wrap">
            <div className="rating">
              <strong>4.9</strong>
              <span>· 87 Google reviews</span>
            </div>
            <div className="rev-grid">
              <blockquote>
                <p>
                  Came to the house, worked the whole day, and the car looked better than it did at delivery. No
                  pressure, no add-ons.
                </p>
                <footer>M. R. · Ellicott City</footer>
              </blockquote>
              <blockquote>
                <p>Explained exactly what a two-step would and would not fix, then did precisely that. Worth every dollar.</p>
                <footer>D. C. · Columbia</footer>
              </blockquote>
              <blockquote>
                <p>Three years on the coating and it still beads. The maintenance visits are the reason.</p>
                <footer>A. P. · Catonsville</footer>
              </blockquote>
            </div>
          </div>
        </section>

        <section id="pricing" className="pricing" data-screen-label="Pricing">
          <div className="wrap">
            <p className="eyebrow">Pricing</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "24ch" }}>Three levels of care</h2>
            <p className="lede">
              Prices are for standard-size cars and sedans. Large SUVs, trucks, and vehicles in heavy condition are
              quoted after assessment.
            </p>
            <div className="tiers">
              <div className="tier">
                <span className="mono">Level I</span>
                <h3>Maintenance</h3>
                <p>Regular care for a vehicle already in good order, coated or not.</p>
                <ul className="lines">
                  <li>
                    <span>Maintenance wash + interior wipe</span>
                    <span className="amt">$140</span>
                  </li>
                  <li>
                    <span>Interior deep clean / extraction</span>
                    <span className="amt">$300</span>
                  </li>
                  <li>
                    <span>Full detail — interior and exterior</span>
                    <span className="amt">$450</span>
                  </li>
                </ul>
                <a href="#enquire" className="btn btn--ghost" onClick={() => setPkg(PACKAGE_OPTIONS[TIER_TO_PACKAGE[0]])}>
                  Request an appointment
                </a>
              </div>
              <div className="tier tier--signature">
                <span className="mono" style={{ color: "var(--wg-brass)" }}>
                  Level II
                </span>
                <h3>Correction + coating</h3>
                <p>Defects removed and the finish protected. The house's most requested work.</p>
                <ul className="lines">
                  <li>
                    <span>1-step correction + sealant</span>
                    <span className="amt">$750</span>
                  </li>
                  <li>
                    <span>New-car prep + ceramic coating</span>
                    <span className="amt">$1,200</span>
                  </li>
                </ul>
                <a href="#enquire" className="btn" onClick={() => setPkg(PACKAGE_OPTIONS[TIER_TO_PACKAGE[1]])}>
                  Request an appointment
                </a>
              </div>
              <div className="tier">
                <span className="mono">Level III</span>
                <h3>Full restoration</h3>
                <p>Two stages of correction on paint that has been neglected, then coated.</p>
                <ul className="lines">
                  <li>
                    <span>2-step correction + ceramic coating</span>
                    <span className="amt">$1,800</span>
                  </li>
                  <li>
                    <span>Multi-day and collection work</span>
                    <span className="amt">On assessment</span>
                  </li>
                </ul>
                <a href="#enquire" className="btn btn--ghost" onClick={() => setPkg(PACKAGE_OPTIONS[TIER_TO_PACKAGE[2]])}>
                  Request an appointment
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="enquire" className="enquire" data-theme="inverse" data-screen-label="Enquiry">
          <div className="wrap">
            <p className="eyebrow" style={{ color: "var(--wg-brass-light)" }}>
              Appointments
            </p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "22ch" }}>Request an appointment</h2>
            <p className="lede">Six fields. A reply with a date and a firm price follows within one working day.</p>
            <div className="form-grid">
              <div className="aside">
                <dl>
                  <div>
                    <dt>Service area</dt>
                    <dd>Ellicott City and 20 miles — Columbia, Catonsville, Elkridge, Clarksville, Fulton, Baltimore, Arbutus, Sykesville.</dd>
                  </div>
                  <div>
                    <dt>Telephone</dt>
                    <dd>
                      <a href="tel:+14105550118">(410) 555-0118</a>
                    </dd>
                  </div>
                  <div>
                    <dt>Email</dt>
                    <dd>
                      <a href="mailto:appointments@foundrydetailing.com">appointments@foundrydetailing.com</a>
                    </dd>
                  </div>
                  <div>
                    <dt>Hours</dt>
                    <dd>Monday to Saturday, 8:00 – 18:00. One vehicle per day.</dd>
                  </div>
                </dl>
              </div>
              <div>
                {!sent ? (
                  <form id="enquiryForm" noValidate onSubmit={onSubmit}>
                    <div className="fields">
                      <div>
                        <label htmlFor="f-name">Name</label>
                        <input
                          id="f-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="First and last"
                          style={missing.has("name") ? { borderBottomColor: "#C99A92" } : undefined}
                        />
                      </div>
                      <div>
                        <label htmlFor="f-phone">Telephone</label>
                        <input
                          id="f-phone"
                          type="tel"
                          autoComplete="tel"
                          value={phone.value}
                          onChange={(e) => phone.onChange(e.target.value)}
                          onBlur={phone.onBlur}
                          placeholder="(000) 000-0000"
                          style={missing.has("phone") || phone.invalid ? { borderBottomColor: "#C99A92" } : undefined}
                        />
                        <div className={`msg${phone.invalid ? " bad" : ""}`}>{phone.message}</div>
                      </div>
                      <div>
                        <label htmlFor="f-email">Email</label>
                        <input
                          id="f-email"
                          type="email"
                          autoComplete="email"
                          value={email.value}
                          onChange={(e) => email.onChange(e.target.value)}
                          onBlur={email.onBlur}
                          placeholder="you@address.com"
                          style={missing.has("email") || email.invalid ? { borderBottomColor: "#C99A92" } : undefined}
                        />
                        <div className={`msg${email.invalid ? " bad" : ""}`}>{email.message}</div>
                      </div>
                      <div>
                        <label htmlFor="f-zip">Service address ZIP</label>
                        <input
                          id="f-zip"
                          inputMode="numeric"
                          maxLength={5}
                          value={zip}
                          onChange={(e) => onZipChange(e.target.value)}
                          placeholder="21043"
                          style={missing.has("zip") ? { borderBottomColor: "#C99A92" } : undefined}
                        />
                        <div className={`msg${zipMsg ? (zipMsg.ok ? " zip-ok" : " bad") : ""}`}>{zipMsg?.text}</div>
                      </div>
                      <div>
                        <label htmlFor="f-vehicle">Vehicle — year, make, model</label>
                        <input
                          id="f-vehicle"
                          value={vehicle}
                          onChange={(e) => setVehicle(e.target.value)}
                          placeholder="2021 Porsche Macan"
                          style={missing.has("vehicle") ? { borderBottomColor: "#C99A92" } : undefined}
                        />
                      </div>
                      <div>
                        <label htmlFor="f-package">Package</label>
                        <select id="f-package" value={pkg} onChange={(e) => setPkg(e.target.value)}>
                          {PACKAGE_OPTIONS.map((o) => (
                            <option key={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="f-window">Preferred date window</label>
                        <select id="f-window" value={dateWindow} onChange={(e) => setDateWindow(e.target.value)}>
                          <option>This week</option>
                          <option>Next week</option>
                          <option>Within the month</option>
                          <option>Flexible</option>
                        </select>
                      </div>
                      <div>
                        <label>Power and water at the address</label>
                        <div className="radios">
                          <label>
                            <input type="radio" name="utilities" checked={utilities === "both"} onChange={() => setUtilities("both")} /> Both
                            available
                          </label>
                          <label>
                            <input type="radio" name="utilities" checked={utilities === "power"} onChange={() => setUtilities("power")} /> Power
                            only
                          </label>
                          <label>
                            <input type="radio" name="utilities" checked={utilities === "none"} onChange={() => setUtilities("none")} /> Neither
                          </label>
                        </div>
                      </div>
                      <div className="span2">
                        <label htmlFor="f-notes">Anything else</label>
                        <textarea
                          id="f-notes"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Paint condition, existing coating, garage or driveway, deadlines."
                        />
                      </div>
                    </div>
                    <div className="form-foot">
                      <button type="submit" className="btn">
                        Submit enquiry
                      </button>
                      <small>Neither is a problem — water and power are carried on the van.</small>
                    </div>
                    {submitError && <p className="msg bad">{submitError}</p>}
                  </form>
                ) : (
                  <div className="sent" id="sent">
                    <h3 style={{ color: "var(--wg-paper)", marginBottom: "var(--space-5)" }}>Received.</h3>
                    <p>
                      The house will reply within one working day with a date and a firm price. For anything urgent,
                      call <a href="tel:+14105550118">(410) 555-0118</a>.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter variant="home" />
    </>
  );
}
